import { ARIA, SELECTORS, CLASS_NAMES, EVENTS, MISC } from '@bs/constants';
import { getYoutubeIdFromUrl, randomSecureString } from '@bs/utils';

/**
 * A class that creates the functionality for Youtube video blocks. A clickable button that expands a player area using the YouTube iframe api
 */
export default class VideoYoutube {
  /**
   * Constructor for YT Video
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * DOM node that is passed into the constructor
     * @property {object} element - DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * ID of the YT video
     * @property {string} id - ID of the YT video
     */
    this.videoId = getYoutubeIdFromUrl(this.element.dataset.url);

    /**
     * Create ID for iframe
     * @property {string} iframeId - initial string for the iframe id
     */
    this.iframeId = 'player-';

    this.playerready = false;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable().scriptInject();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.playTrigger = this.element.querySelector(SELECTORS.VIDEO_TRIGGER);
    this.featuredVideoEmbed = this.element.querySelector(SELECTORS.VIDEO_EMBED);
    this.iframeId += randomSecureString(16);
    if (this.featuredVideoEmbed) {
      this.featuredVideoEmbed.setAttribute('id', this.iframeId);
    } else {
      // eslint-disable-next-line no-console
      console.warn('[VideoYoutube] Missing embed container', this.element);
    }

    return this;
  }

  /**
   * scriptInject
   *
   * Pull the YT iframe API if the object doesn't exist
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  scriptInject() {
    const tag = document.createElement('script');

    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    setTimeout(() => {
      this.onYouTubeIframeAPIReady();
    }, 2000);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onClickPlayTriggerYTHandler = this.onClickPlayTriggerYT.bind(this);
    this.onPlayerStateChange = this.onPlayerStateChange.bind(this);
    this.pauseHandler = this.pause.bind(this);
    this.onModalOpenHandler = this.onModalOpen.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} From A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.playTrigger) {
      this.playTrigger.addEventListener(
        EVENTS.CLICK,
        this.onClickPlayTriggerYTHandler,
        false
      );
    } else {
      // eslint-disable-next-line no-console
      console.warn('[VideoYoutube] Missing play trigger', this.element);
    }
    document.addEventListener(EVENTS.PAUSE_VIDEO, this.pauseHandler, false);
    document.addEventListener(
      EVENTS.MODAL_OPEN,
      this.onModalOpenHandler,
      false
    );

    return this;
  }

  /**
   * Serialize data
   *
   */
  onYouTubeIframeAPIReady() {
    /* eslint no-undef: 0 */
    if (!this.featuredVideoEmbed) return;
    this.player = new YT.Player(this.iframeId, {
      videoId: this.videoId,
      events: {
        onReady: () => {
          this.playerready = true;
          // If autoplay was requested before player was ready
          if (this.deferredAutoplay === true) {
            this.forceUiToPlayingState();
            this.player.playVideo();
            this.deferredAutoplay = false;
          }
        },
        onStateChange: this.onPlayerStateChange
      },
      playerVars: { rel: 0, fs: 1, showinfo: 0 }
    });
  }

  /**
   * Detect if player video has ended and fade back to preview image.
   * @param {event} event - DOM event
   */
  onPlayerStateChange(event) {
    if (event.data === 0) {
      this.element.classList.toggle(CLASS_NAMES.PLAYING);
      this.playTrigger.classList.toggle(CLASS_NAMES.CLICK);
      this.playTrigger.setAttribute(ARIA.HIDDEN, false);
      this.replaceIframeElement = document.createElement(SELECTORS.DIV);
      this.replaceIframeElement.setAttribute('id', this.iframeId);
      this.replaceIframeElement.classList.add(CLASS_NAMES.VIDEO_EMBED);
      this.featuredVideoEmbed.setAttribute(MISC.TABINDEX, -1);
      this.element
        .querySelector(SELECTORS.VIDEO_EMBED)
        .replaceWith(this.replaceIframeElement);
      this.featuredVideoEmbed = this.element.querySelector(
        SELECTORS.VIDEO_EMBED
      );
    }
  }

  /**
   * Click the button
   * @returns {object} A reference to the current instance of this class
   * @chainable
   * @param {event} event - DOM event
   */
  onClickPlayTriggerYT(event) {
    event.preventDefault();
    if (!this.playTrigger || !this.featuredVideoEmbed) return this;
    this.element.classList.toggle(CLASS_NAMES.PLAYING);
    this.playTrigger.classList.toggle(CLASS_NAMES.CLICK);
    this.playTrigger.setAttribute(ARIA.HIDDEN, true);
    this.featuredVideoEmbed.setAttribute(MISC.TABINDEX, 0);

    if (this.playerready) {
      this.player.playVideo();
    }

    return this;
  }

  /**
   * When a modal opens, start playback or defer until ready.
   */
  onModalOpen() {
    const parentModal = this.element.closest('.modal');
    if (!parentModal || !parentModal.classList.contains('is-active')) return;
    // If already playing state, do nothing
    if (this.element.classList.contains(CLASS_NAMES.PLAYING)) return;
    // If player not ready yet, defer autoplay
    if (!this.playerready) {
      this.deferredAutoplay = true;
      this.forceUiToPlayingState();
      return;
    }
    // Ready: trigger playback immediately
    this.forceUiToPlayingState();
    this.player.playVideo();
  }

  /**
   * Apply the same UI state changes as clicking play
   */
  forceUiToPlayingState() {
    this.element.classList.add(CLASS_NAMES.PLAYING);
    if (this.playTrigger) {
      this.playTrigger.classList.add(CLASS_NAMES.CLICK);
      this.playTrigger.setAttribute(ARIA.HIDDEN, true);
    }
    if (this.featuredVideoEmbed) {
      this.featuredVideoEmbed.setAttribute(MISC.TABINDEX, 0);
    }
  }

  /**
   * Pause a given video
   * @param {event} event - DOM event
   * @returns {void}
   */
  pause(event) {
    if (event.detail !== this.videoId) return;
    if (this.player && typeof this.player.stopVideo === 'function') {
      try {
        this.player.stopVideo();
      } catch (e) {
        // ignore
      }
    }
  }
}
